import * as i0 from '@angular/core';
import { inject, ElementRef, Renderer2, EventEmitter, Output, Input, HostBinding, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import * as i1 from '@tc/interstellar-icon';
import { IconTranslationService, StlrIconModule } from '@tc/interstellar-icon';
import { OverlayModule } from '@angular/cdk/overlay';
import { StlrFormModule } from '@tc/interstellar-form';
const _c0 = ["*"];
function StlrNotificationComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 4);
    i0.ɵɵlistener("click", function StlrNotificationComponent_Conditional_5_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.close());
    });
    i0.ɵɵelementStart(1, "stlr-icon");
    i0.ɵɵtext(2, "close");
    i0.ɵɵelementEnd()();
  }
}
function StlrStatusComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "stlr-icon", 0);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.icon);
  }
}
class StlrNotificationComponent {
  constructor() {
    this.elementRef = inject(ElementRef);
    this.renderer = inject(Renderer2);
    this.iconTranslationService = inject(IconTranslationService);
    this._type = 'info';
    /** Displays a close button. You must handle the 'closed' event. */
    this.showCloseButton = false;
    /** The number of milliseconds to display this notification. You must handle the 'closed' event. */
    this.displayFor = 5000;
    this.closed = new EventEmitter();
  }
  get warnClass() {
    return this.type === 'warn';
  }
  get errorClass() {
    return this.type === 'error';
  }
  get infoClass() {
    return this.type === 'info';
  }
  get successClass() {
    return this.type === 'success';
  }
  /** Changes the appearance of the notification */
  get type() {
    return this._type;
  }
  set type(type) {
    this._type = type;
  }
  set open(isOpen) {
    if (isOpen) {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'closed');
    } else {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'closed', '');
    }
  }
  get icon() {
    return this.iconTranslationService.getIconForColor(this.type);
  }
  close() {
    this.closed.emit();
  }
  ngOnInit() {
    if (this.displayFor) {
      setTimeout(() => this.close(), this.displayFor);
    }
  }
  static {
    this.ɵfac = function StlrNotificationComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrNotificationComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: StlrNotificationComponent,
      selectors: [["stlr-notification"]],
      hostVars: 8,
      hostBindings: function StlrNotificationComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("warn", ctx.warnClass)("error", ctx.errorClass)("info", ctx.infoClass)("success", ctx.successClass);
        }
      },
      inputs: {
        type: "type",
        open: "open",
        showCloseButton: "showCloseButton",
        displayFor: "displayFor"
      },
      outputs: {
        closed: "closed"
      },
      standalone: false,
      ngContentSelectors: _c0,
      decls: 6,
      vars: 2,
      consts: [[1, "stlr-notification__icon--container"], [1, "stlr-notification__icon"], [1, "stlr-notification__copy"], [1, "close-icon"], [1, "close-icon", 3, "click"]],
      template: function StlrNotificationComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 0)(1, "stlr-icon", 1);
          i0.ɵɵtext(2);
          i0.ɵɵelementEnd()();
          i0.ɵɵelementStart(3, "div", 2);
          i0.ɵɵprojection(4);
          i0.ɵɵelementEnd();
          i0.ɵɵconditionalCreate(5, StlrNotificationComponent_Conditional_5_Template, 3, 0, "button", 3);
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵtextInterpolate(ctx.icon);
          i0.ɵɵadvance(3);
          i0.ɵɵconditional(ctx.showCloseButton ? 5 : -1);
        }
      },
      dependencies: [i1.StlrIconComponent],
      styles: ["[_nghost-%COMP%]{display:flex;align-items:center;position:relative;height:auto;width:100%;border:solid 1px;border-radius:var(--stlr-border-radius);color:#fff;overflow:hidden;background-color:#fafafa;font-weight:700}.warn[_nghost-%COMP%]{border:1px solid var(--stlr-color__warn)}.warn[_nghost-%COMP%]   .stlr-notification__icon--container[_ngcontent-%COMP%]{background-color:var(--stlr-color__warn)}.warn[_nghost-%COMP%]   .stlr-notification__icon[_ngcontent-%COMP%]{font-weight:700;color:#fff}.warn[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   a[_ngcontent-%COMP%], .warn[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{color:var(--stlr-color__warn--dark)}.success[_nghost-%COMP%]{border:1px solid var(--stlr-color__success)}.success[_nghost-%COMP%]   .stlr-notification__icon--container[_ngcontent-%COMP%]{background-color:var(--stlr-color__success)}.success[_nghost-%COMP%]   .stlr-notification__icon[_ngcontent-%COMP%]{color:#fff}.success[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   a[_ngcontent-%COMP%], .success[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{color:var(--stlr-color__success--dark)}.info[_nghost-%COMP%]{border:1px solid var(--stlr-color__info)}.info[_nghost-%COMP%]   .stlr-notification__icon--container[_ngcontent-%COMP%]{background-color:var(--stlr-color__info)}.info[_nghost-%COMP%]   .stlr-notification__icon[_ngcontent-%COMP%]{color:#fff}.info[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   a[_ngcontent-%COMP%], .info[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{color:var(--stlr-color__info--dark)}.error[_nghost-%COMP%]{border:1px solid var(--stlr-color__error)}.error[_nghost-%COMP%]   .stlr-notification__icon--container[_ngcontent-%COMP%]{background-color:var(--stlr-color__error)}.error[_nghost-%COMP%]   .stlr-notification__icon[_ngcontent-%COMP%]{color:#fff}.error[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   a[_ngcontent-%COMP%], .error[_nghost-%COMP%]   .stlr-notification__copy[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{color:var(--stlr-color__error--dark)}[closed][_nghost-%COMP%]{display:none}.close-icon[_ngcontent-%COMP%]{appearance:none;outline:none;border:0;background:transparent;cursor:pointer;padding:10px}.stlr-notification__icon--container[_ngcontent-%COMP%]{display:flex;justify-content:center;position:relative;padding:var(--space--8);align-items:center;z-index:1;align-self:stretch}.stlr-notification__icon[_ngcontent-%COMP%]{-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\";font-variation-settings:\"FILL\" 0,\"wght\" 400,\"GRAD\" 0,\"opsz\" 48}.stlr-notification__copy[_ngcontent-%COMP%]{flex-grow:1;position:relative;padding:var(--space--12);font-family:var(--stlr-font-family--copy);font-size:14px;color:var(--stlr-color__copy);z-index:1}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrNotificationComponent, [{
    type: Component,
    args: [{
      selector: 'stlr-notification',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: false,
      template: "<div class=\"stlr-notification__icon--container\">\n  <stlr-icon class=\"stlr-notification__icon\">{{ icon }}</stlr-icon>\n</div>\n<div class=\"stlr-notification__copy\">\n  <ng-content></ng-content>\n</div>\n@if (showCloseButton) {\n  <button class=\"close-icon\" (click)=\"close()\">\n    <stlr-icon>close</stlr-icon>\n  </button>\n}\n",
      styles: [":host{display:flex;align-items:center;position:relative;height:auto;width:100%;border:solid 1px;border-radius:var(--stlr-border-radius);color:#fff;overflow:hidden;background-color:#fafafa;font-weight:700}:host.warn{border:1px solid var(--stlr-color__warn)}:host.warn .stlr-notification__icon--container{background-color:var(--stlr-color__warn)}:host.warn .stlr-notification__icon{font-weight:700;color:#fff}:host.warn .stlr-notification__copy a,:host.warn .stlr-notification__copy button{color:var(--stlr-color__warn--dark)}:host.success{border:1px solid var(--stlr-color__success)}:host.success .stlr-notification__icon--container{background-color:var(--stlr-color__success)}:host.success .stlr-notification__icon{color:#fff}:host.success .stlr-notification__copy a,:host.success .stlr-notification__copy button{color:var(--stlr-color__success--dark)}:host.info{border:1px solid var(--stlr-color__info)}:host.info .stlr-notification__icon--container{background-color:var(--stlr-color__info)}:host.info .stlr-notification__icon{color:#fff}:host.info .stlr-notification__copy a,:host.info .stlr-notification__copy button{color:var(--stlr-color__info--dark)}:host.error{border:1px solid var(--stlr-color__error)}:host.error .stlr-notification__icon--container{background-color:var(--stlr-color__error)}:host.error .stlr-notification__icon{color:#fff}:host.error .stlr-notification__copy a,:host.error .stlr-notification__copy button{color:var(--stlr-color__error--dark)}:host[closed]{display:none}.close-icon{appearance:none;outline:none;border:0;background:transparent;cursor:pointer;padding:10px}.stlr-notification__icon--container{display:flex;justify-content:center;position:relative;padding:var(--space--8);align-items:center;z-index:1;align-self:stretch}.stlr-notification__icon{-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\";font-variation-settings:\"FILL\" 0,\"wght\" 400,\"GRAD\" 0,\"opsz\" 48}.stlr-notification__copy{flex-grow:1;position:relative;padding:var(--space--12);font-family:var(--stlr-font-family--copy);font-size:14px;color:var(--stlr-color__copy);z-index:1}\n"]
    }]
  }], null, {
    warnClass: [{
      type: HostBinding,
      args: ['class.warn']
    }],
    errorClass: [{
      type: HostBinding,
      args: ['class.error']
    }],
    infoClass: [{
      type: HostBinding,
      args: ['class.info']
    }],
    successClass: [{
      type: HostBinding,
      args: ['class.success']
    }],
    type: [{
      type: Input
    }],
    open: [{
      type: Input
    }],
    showCloseButton: [{
      type: Input
    }],
    displayFor: [{
      type: Input
    }],
    closed: [{
      type: Output
    }]
  });
})();
class StlrStatusComponent {
  constructor() {
    this.hostClass = true;
    this._iconTranslationService = inject(IconTranslationService);
    this.status = null;
  }
  get icon() {
    if (this.status) {
      return this._iconTranslationService.getIconForColor(this.status);
    }
    return undefined;
  }
  get isWarn() {
    return this.status === 'warn';
  }
  get isSuccess() {
    return this.status === 'success';
  }
  get isInfo() {
    return this.status === 'info';
  }
  get isError() {
    return this.status === 'error';
  }
  static {
    this.ɵfac = function StlrStatusComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrStatusComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: StlrStatusComponent,
      selectors: [["stlr-status"]],
      hostVars: 10,
      hostBindings: function StlrStatusComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("stlr-status", ctx.hostClass)("warn", ctx.isWarn)("success", ctx.isSuccess)("info", ctx.isInfo)("error", ctx.isError);
        }
      },
      inputs: {
        status: "status"
      },
      standalone: false,
      ngContentSelectors: _c0,
      decls: 2,
      vars: 1,
      consts: [["size", "med"]],
      template: function StlrStatusComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵconditionalCreate(0, StlrStatusComponent_Conditional_0_Template, 2, 1, "stlr-icon", 0);
          i0.ɵɵprojection(1);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.status ? 0 : -1);
        }
      },
      dependencies: [i1.StlrIconComponent],
      styles: ["[_nghost-%COMP%]{--status__font-size: var(--font--14);--status__icon-size: var(--space--24);--status__icon--warn: \"warning\";--status__icon--error: \"cancel\";--status__icon--info: \"info\";--status__icon--success: \"check_circle\";--status__icon--disabled: \"not_interested\";--status__bg: #fafafa;--status__color--success: var(--stlr-color__success--dark);--status__color--warn: var(--stlr-color__warn--dark);--status__color--error: var(--stlr-color__error--dark);--status__color--disabled: var(--stlr-color__disabled--dark);--status__color--info: var(--stlr-color__info--dark);--status__border--success: var(--stlr-color__success--light);--status__border--warn: var(--stlr-color__warn--light);--status__border--error: var(--stlr-color__error--light);--status__border--disabled: var(--stlr-color__disabled--light);--status__border--info: var(--stlr-color__info--light);display:flex;align-items:center;gap:var(--space--4);line-height:1;font-size:var(--status__font-size);line-height:1.3;padding:var(--space--8) var(--space--16) var(--space--8) var(--space--8);border-radius:4px;font-weight:700}[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{left:0;top:0;bottom:0;height:var(--status__icon-size);width:var(--status__icon-size)}.success[_nghost-%COMP%]{background-color:var(--status__bg);border:solid 1px var(--status__border--success)}.success[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{color:var(--status__color--success)}.warn[_nghost-%COMP%]{background-color:var(--status__bg);border:solid 1px var(--status__border--warn)}.warn[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{color:var(--status__color--warn)}.error[_nghost-%COMP%]{background-color:var(--status__bg);border:solid 1px var(--status__border--error)}.error[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{color:var(--status__color--error)}.disabled[_nghost-%COMP%]{background-color:var(--status__bg);border:solid 1px var(--status__border--disabled)}.disabled[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{color:var(--status__color--disabled)}.info[_nghost-%COMP%]{background-color:var(--status__bg);border:solid 1px var(--status__border--info)}.info[_nghost-%COMP%]   .stlr-icon[_ngcontent-%COMP%]{color:var(--status__color--info)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrStatusComponent, [{
    type: Component,
    args: [{
      selector: 'stlr-status',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: false,
      template: "@if (status) {\n  <stlr-icon size=\"med\">{{ icon }}</stlr-icon>\n}\n<ng-content></ng-content>\n",
      styles: [":host{--status__font-size: var(--font--14);--status__icon-size: var(--space--24);--status__icon--warn: \"warning\";--status__icon--error: \"cancel\";--status__icon--info: \"info\";--status__icon--success: \"check_circle\";--status__icon--disabled: \"not_interested\";--status__bg: #fafafa;--status__color--success: var(--stlr-color__success--dark);--status__color--warn: var(--stlr-color__warn--dark);--status__color--error: var(--stlr-color__error--dark);--status__color--disabled: var(--stlr-color__disabled--dark);--status__color--info: var(--stlr-color__info--dark);--status__border--success: var(--stlr-color__success--light);--status__border--warn: var(--stlr-color__warn--light);--status__border--error: var(--stlr-color__error--light);--status__border--disabled: var(--stlr-color__disabled--light);--status__border--info: var(--stlr-color__info--light);display:flex;align-items:center;gap:var(--space--4);line-height:1;font-size:var(--status__font-size);line-height:1.3;padding:var(--space--8) var(--space--16) var(--space--8) var(--space--8);border-radius:4px;font-weight:700}:host .stlr-icon{left:0;top:0;bottom:0;height:var(--status__icon-size);width:var(--status__icon-size)}:host.success{background-color:var(--status__bg);border:solid 1px var(--status__border--success)}:host.success .stlr-icon{color:var(--status__color--success)}:host.warn{background-color:var(--status__bg);border:solid 1px var(--status__border--warn)}:host.warn .stlr-icon{color:var(--status__color--warn)}:host.error{background-color:var(--status__bg);border:solid 1px var(--status__border--error)}:host.error .stlr-icon{color:var(--status__color--error)}:host.disabled{background-color:var(--status__bg);border:solid 1px var(--status__border--disabled)}:host.disabled .stlr-icon{color:var(--status__color--disabled)}:host.info{background-color:var(--status__bg);border:solid 1px var(--status__border--info)}:host.info .stlr-icon{color:var(--status__color--info)}\n"]
    }]
  }], null, {
    hostClass: [{
      type: HostBinding,
      args: ['class.stlr-status']
    }],
    status: [{
      type: Input
    }],
    isWarn: [{
      type: HostBinding,
      args: ['class.warn']
    }],
    isSuccess: [{
      type: HostBinding,
      args: ['class.success']
    }],
    isInfo: [{
      type: HostBinding,
      args: ['class.info']
    }],
    isError: [{
      type: HostBinding,
      args: ['class.error']
    }]
  });
})();

// noinspection ES6PreferShortImport
const publicApi = [StlrNotificationComponent, StlrStatusComponent];
class StlrNotificationModule {
  static {
    this.ɵfac = function StlrNotificationModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrNotificationModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: StlrNotificationModule,
      declarations: [StlrNotificationComponent, StlrStatusComponent],
      imports: [StlrFormModule, A11yModule, CommonModule, OverlayModule, StlrIconModule],
      exports: [StlrNotificationComponent, StlrStatusComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [IconTranslationService],
      imports: [StlrFormModule, A11yModule, CommonModule, OverlayModule, StlrIconModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrNotificationModule, [{
    type: NgModule,
    args: [{
      imports: [StlrFormModule, A11yModule, CommonModule, OverlayModule, StlrIconModule],
      declarations: [...publicApi],
      exports: [...publicApi],
      providers: [IconTranslationService]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { StlrNotificationComponent, StlrNotificationModule, StlrStatusComponent };
